var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { ReflectionProbe as BABYLON_ReflectionProbe } from '@inter-ikea-betrakta-babylonjs/core/Probes/reflectionProbe.js';
import { Vector3 as BABYLON_Vector3 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { ViewerMaterial } from '../material/ViewerMaterial.js';
import { IBoundingBox, IVector3 } from '../../api.js';
import { Debug } from '../../utils/DebugLayer/utils/DebugDecorators.js';
import { Material } from '../Material.js';
import { PBRMaterial as BABYLON_PBRMaterial } from '@inter-ikea-betrakta-babylonjs/core/Materials/PBR/pbrMaterial.js';
import { ReflectionProbeVisualizer } from './ReflectionProbeVisualizer.js';
import { Logger } from '../../logging/Logger.js';
let probe_count = 0;
const LOGGER_CATEGORY = 'ReflectionProbe';
let ReflectionProbe = class ReflectionProbe {
    constructor(size, _viewer) {
        this.size = size;
        this._viewer = _viewer;
        this._is_dirty = true;
        this._debug_geometry = null;
        this._source_objects = new Set();
        this._target_objects = new Set();
        this._probe = new BABYLON_ReflectionProbe('reflection_probe_' + probe_count, size, _viewer.scene, undefined, undefined, true);
        // For some reason our reflections gets inverted so we just invert it here...
        // @ts-expect-error
        this._probe._renderTargetTexture.invertZ = !this._probe._renderTargetTexture.invertZ;
        this._probe.position = new BABYLON_Vector3(0, 0, 0);
        this._probe.refreshRate = 0;
        probe_count++;
        this._viewer.reflection_probes.push(this);
    }
    get source_objects() {
        return Array.from(this._source_objects);
    }
    get target_objects() {
        return Array.from(this._target_objects);
    }
    get position() {
        return this._probe.position;
    }
    set position(val) {
        if (IVector3.equals(val, this._probe.position))
            return;
        this._probe.position.fromIVector3(val);
        this.mark_dirty();
    }
    add_models_as_source(object_ids) {
        object_ids.forEach(id => {
            this._source_objects.add(id);
            const scene_object = this._viewer.viewer_scene.objects.get(id);
            scene_object.changed_observable.add(() => {
                this.mark_dirty();
            });
            scene_object.ready.then(() => {
                var _a;
                (_a = scene_object.mesh_nodes) === null || _a === void 0 ? void 0 : _a.forEach(mesh => {
                    this._probe.renderList.push(mesh);
                    this.mark_dirty();
                });
            }).catch(() => {
                // Silence?
            });
        });
    }
    add_models_as_target(object_ids) {
        object_ids.forEach(id => {
            this._target_objects.add(id);
            const scene_object = this._viewer.viewer_scene.objects.get(id);
            scene_object.ready.then(() => {
                var _a;
                (_a = scene_object._asset_instance) === null || _a === void 0 ? void 0 : _a.instance_iterator((mesh) => {
                    var _a;
                    const custom_material = (_a = mesh.get_custom_material()) !== null && _a !== void 0 ? _a : mesh.get_source_material();
                    if (custom_material instanceof Material) {
                        custom_material.ready.then(() => {
                            if (custom_material.material !== null && custom_material.material.babylon_material instanceof BABYLON_PBRMaterial) {
                                // TODO: Betrakta material shader support
                                custom_material.material.babylon_material.reflectionTexture = this._probe.cubeTexture;
                            }
                            else {
                                Logger.as(LOGGER_CATEGORY).warn("Can't apply reflection probe texture, incompatible material type");
                            }
                        }).catch(() => { });
                    }
                    else if (custom_material !== null && custom_material instanceof ViewerMaterial) {
                        custom_material.reflectionTexture = this._probe.cubeTexture;
                    }
                    else if (mesh.babylon_instance.sourceMesh.material instanceof ViewerMaterial) {
                        mesh.babylon_instance.sourceMesh.material.reflectionTexture = this._probe.cubeTexture;
                    }
                    else {
                        Logger.as(LOGGER_CATEGORY).error('Error: no material to apply reflection probe to.');
                    }
                });
            }).catch(() => {
                // Silence?
            });
        });
    }
    update(force_render = false) {
        var _a;
        if (!force_render && !this._is_dirty)
            return;
        this.render_internal();
        (_a = this._debug_geometry) === null || _a === void 0 ? void 0 : _a.update();
    }
    render() {
        this.mark_dirty();
    }
    create_debug_geometry() {
        if (this._debug_geometry)
            return;
        this._debug_geometry = new ReflectionProbeVisualizer(this, this._viewer);
    }
    remove_debug_geometry() {
        var _a;
        (_a = this._debug_geometry) === null || _a === void 0 ? void 0 : _a.dispose();
        this._debug_geometry = null;
    }
    mark_dirty() {
        this._is_dirty = true;
        this._viewer.mark_scene_dirty();
    }
    render_internal() {
        var _a;
        this._is_dirty = false;
        this._viewer.scene.incrementRenderId();
        this._probe.cubeTexture.render();
        this._probe.cubeTexture.boundingBoxPosition.copyFromFloats(this.position.x, this.position.y, this.position.z);
        const bounds = [];
        (_a = this._probe.renderList) === null || _a === void 0 ? void 0 : _a.forEach(mesh => {
            const bounding_info = mesh.getBoundingInfo();
            bounds.push({ min: bounding_info.minimum, max: bounding_info.maximum });
        });
        const size = IBoundingBox.size(IBoundingBox.union(...bounds));
        this._probe.cubeTexture.boundingBoxSize = new BABYLON_Vector3(size.x, size.y, size.z);
    }
    dispose() {
        this._probe.dispose();
        // To make sure probe can get GCed we set it to undefined, even though it should always be an object
        // @ts-ignore
        this._probe = undefined;
        const index = this._viewer.reflection_probes.indexOf(this);
        if (index >= 0) {
            this._viewer.reflection_probes.splice(index, 1);
        }
    }
};
ReflectionProbe = __decorate([
    Debug.Selectable({
        name: 'ReflectionProbe',
        icon: '☇',
        tooltip: (i) => i._probe.name,
        world_position: (i) => i.position
    })
], ReflectionProbe);
export { ReflectionProbe };
//# sourceMappingURL=ReflectionProbe.js.map